import { Box, SxProps } from '@ltvco/refresh-lib/theme';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';

import { StyledClearIconButton } from './components';

export interface WithClearButtonProps {
  value?: string;
  onClear?: () => void;
  showClearButton?: boolean;
  clearButtonStyles?: SxProps;
}

/**
 * HOC that adds a clear button to an input component
 * The clear button appears when the input has a value
 *
 * @param Component The component to enhance with a clear button
 * @returns A new component with a clear button
 */
export const withClearButton = <P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P & WithClearButtonProps> => {
  return ({
    value,
    onClear,
    showClearButton = true,
    clearButtonStyles = {},
    ...props
  }: WithClearButtonProps & P) => {
    const shouldShowClearButton = showClearButton && !!value;

    return (
      <Box sx={{ position: 'relative', width: '100%' }}>
        <Component {...(props as P)} />

        {shouldShowClearButton && onClear && (
          <StyledClearIconButton
            className="clear-button"
            onClick={onClear}
            sx={{ ...clearButtonStyles }}
            size="small"
            data-testid="clear-button"
          >
            <CloseIcon fontSize="small" />
          </StyledClearIconButton>
        )}
      </Box>
    );
  };
};
